import axios from "axios";
import type mapboxgl from 'mapbox-gl';

import { useContext, useEffect, useState, useCallback } from "react";
import type { DrawnPartition, PartitionFeature, XY } from "../lib/geo/types";
import { ringLonLatToPixels } from "../lib/geo/projection";
import { API_ENDPOINT } from "../configurations/global.config";
import { Polygon } from "geojson";
import { AuthContext } from "../contexts/user-context/user-context";

export function usePartitions(
  project: d3.GeoProjection,
  colorFallback: string,
  branchId?: string | number,
  enabled = true,
  getMapboxMap?: () => mapboxgl.Map | null
) {
  const [serverPartitions, setServerPartitions] = useState<DrawnPartition[]>([]);
  const [loading, setLoading] = useState(false);
  const { config } = useContext(AuthContext);

  const fetchPartitions = async (branchId?: string | number) => {
    const url = branchId ? `${API_ENDPOINT}/api/v1/partitions/?branch=${branchId}` : `${API_ENDPOINT}/api/v1/partitions/`;
    const { data } = await axios.get(url, config);
    // Accept FeatureCollection or plain array
    if (data?.type === "FeatureCollection") {
      return data.features as PartitionFeature[];
    }
    return data as PartitionFeature[];
  }

  const deletePartition = async (id: string | number) => {
    await axios.delete(`${API_ENDPOINT}/api/v1/partitions/${id}/`, config);
    // prune local cache
    setServerPartitions(prev => prev.filter(p => p.id !== String(id)));
  };
  
  const postPartition =  async (payload: {
    branch: string | number;
    label?: string;
    color_hex?: string;
    geom: Polygon;
  }) => {
    const { data } = await axios.post(`${API_ENDPOINT}/api/v1/partitions/`, payload, config);
    return data as PartitionFeature;
  }
  

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const features = await fetchPartitions(branchId);
      const mapInstance = getMapboxMap ? getMapboxMap() : null;
      
      const mapped: DrawnPartition[] = (features || []).map((f: PartitionFeature) => {
        const ring = f.geom.coordinates[0]; // outer ring
        
        // Use Mapbox projection if available, otherwise fall back to D3
        let pixels: XY[];
        if (mapInstance) {
          pixels = ring.map(([lng, lat]) => {
            const point = mapInstance.project([lng, lat]);
            return { x: point.x, y: point.y };
          });
        } else {
          pixels = ringLonLatToPixels(project, ring);
        }
        
        return {
          id: String(f.id),
          points: pixels,
          color: (f.properties?.color_hex as string) || colorFallback,
          label: (f.properties?.label as string) || "",
        };
      });
      setServerPartitions(mapped);
    } finally {
      setLoading(false);
    }
  }, [branchId, project, colorFallback, getMapboxMap]);
  // Note: mapboxMap intentionally excluded to prevent infinite loops

  useEffect(() => {
    if (enabled) {
      reload();
    }
  }, [enabled, branchId, reload]);

  const addFromServer = useCallback((f: PartitionFeature) => {
    const ring = f.geom.coordinates[0];
    const mapInstance = getMapboxMap ? getMapboxMap() : null;
    
    // Use Mapbox projection if available, otherwise fall back to D3
    let pixels: XY[];
    if (mapInstance) {
      pixels = ring.map(([lng, lat]) => {
        const point = mapInstance.project([lng, lat]);
        return { x: point.x, y: point.y };
      });
    } else {
      pixels = ringLonLatToPixels(project, ring);
    }
    
    setServerPartitions((prev) => [
      ...prev,
      {
        id: String(f.id),
        points: pixels,
        color: (f.properties?.color_hex as string) || colorFallback,
        label: (f.properties?.label as string) || "",
      },
    ]);
  }, [project, colorFallback, getMapboxMap]);

  return { serverPartitions, setServerPartitions, addFromServer, reload, loading, postPartition, deletePartition };
}
